import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAttorney } from '../../context/AttorneyContext';
import { useNotifications } from '../../../context/NotificationContext';
import { usePendingMessages, useMessages } from '../../hooks/useMessaging';
import MessageComposeModal from './MessageComposeModal';
import './ClientMessages.css';

/**
 * Client Messages Component
 * Shows notifications and messages sent to clients with their responses
 *
 * Combines:
 * - System notifications (status updates, assignments, deadlines)
 * - Client messages (info requests, responses)
 *
 * Only visible to MarkoTM (internal) attorneys who can message clients directly.
 * External attorneys see "MarkoTM Responses" instead.
 */
function ClientMessages() {
  const { t } = useTranslation('attorney');
  const { canMessageClients } = useAttorney();
  const [activeTab, setActiveTab] = useState('all');
  const [isComposeOpen, setIsComposeOpen] = useState(false);

  // Get notifications from context
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  // Get messages from API
  const { data: pendingData, isLoading: pendingLoading } = usePendingMessages();
  const { data: allData, isLoading: allLoading } = useMessages();

  const pendingMessages = pendingData?.messages || [];
  const allMessages = allData?.messages || [];

  // Combine notifications and messages
  const allItems = useMemo(() => {
    // Messages sent to clients
    const messageItems = allMessages.map(m => ({
      ...m,
      itemType: 'message',
      isMessage: true,
      displayTitle: m.title || m.subject,
      displayMessage: m.message || m.body,
      createdAt: m.createdAt
    }));

    // Notifications (exclude ones that are also in messages to avoid duplicates)
    const messageIds = new Set(allMessages.map(m => m.id));
    const notificationItems = notifications
      .filter(n => !messageIds.has(n.id))
      .map(n => ({
        ...n,
        itemType: 'notification',
        isMessage: false,
        displayTitle: n.title,
        displayMessage: n.message || n.body,
        createdAt: n.createdAt
      }));

    // Combine and sort by date
    return [...messageItems, ...notificationItems].sort((a, b) =>
      new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [allMessages, notifications]);

  // Filter items based on tab
  const unreadItems = allItems.filter(item => !item.read && !item.readAt);
  const displayItems = activeTab === 'pending' ? pendingMessages.map(m => ({
    ...m,
    itemType: 'message',
    isMessage: true,
    displayTitle: m.title || m.subject,
    displayMessage: m.message || m.body
  })) :
    activeTab === 'unread' ? unreadItems :
    allItems;

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const handleMarkAsRead = async (itemId) => {
    await markAsRead(itemId);
  };

  // Access check
  if (!canMessageClients) {
    return (
      <div className="client-messages-page">
        <div className="access-denied">
          <h2>Access Restricted</h2>
          <p>
            Only MarkoTM attorneys can message clients directly.
            External attorneys should use "Request via MarkoTM" to request information.
          </p>
        </div>
      </div>
    );
  }

  const isLoading = pendingLoading || allLoading;

  return (
    <div className="client-messages-page">
      <div className="page-header">
        <div>
          <h1>{t('attorney.notifications.title', 'Notifications')}</h1>
          <p className="page-subtitle">
            {t('attorney.notifications.subtitle', 'System alerts and client message responses')}
          </p>
        </div>
        <div className="header-actions">
          {unreadCount > 0 && (
            <button
              className="btn btn-text"
              onClick={handleMarkAllRead}
            >
              ✓ {t('attorney.notifications.markAllRead', 'Mark all as read')}
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={() => setIsComposeOpen(true)}
          >
            {t('attorney.clientMessages.compose', 'New Message')}
          </button>
        </div>
      </div>

      <div className="messages-tabs">
        <button
          className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          {t('attorney.clientMessages.awaitingResponse', 'Awaiting Response')}
          {pendingMessages.length > 0 && (
            <span className="tab-badge">{pendingMessages.length}</span>
          )}
        </button>
        <button
          className={`tab-btn ${activeTab === 'unread' ? 'active' : ''}`}
          onClick={() => setActiveTab('unread')}
        >
          {t('attorney.notifications.unread', 'Unread')}
          {unreadItems.length > 0 && (
            <span className="tab-badge tab-badge-blue">{unreadItems.length}</span>
          )}
        </button>
        <button
          className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          {t('attorney.notifications.all', 'All')}
          <span className="tab-count">{allItems.length}</span>
        </button>
      </div>

      <div className="messages-list">
        {isLoading ? (
          <div className="loading-state">Loading...</div>
        ) : displayItems.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              {activeTab === 'pending' ? '📨' : activeTab === 'unread' ? '✅' : '📭'}
            </div>
            <h3>
              {activeTab === 'pending'
                ? t('attorney.clientMessages.noPending', 'No messages awaiting response')
                : activeTab === 'unread'
                ? t('attorney.notifications.noUnread', 'All caught up!')
                : t('attorney.notifications.noNotifications', 'No notifications yet')}
            </h3>
            <p>
              {activeTab === 'pending'
                ? t('attorney.clientMessages.noPendingHint', 'Messages you send to clients will appear here until they respond.')
                : activeTab === 'unread'
                ? t('attorney.notifications.noUnreadHint', 'You have no unread notifications.')
                : t('attorney.notifications.noNotificationsHint', 'System alerts and client responses will appear here.')}
            </p>
          </div>
        ) : (
          displayItems.map((item) => (
            item.isMessage ? (
              <MessageCard
                key={item.id}
                message={item}
                onMarkRead={() => handleMarkAsRead(item.id)}
              />
            ) : (
              <NotificationCard
                key={item.id}
                notification={item}
                onMarkRead={() => handleMarkAsRead(item.id)}
              />
            )
          ))
        )}
      </div>

      {isComposeOpen && (
        <MessageComposeModal
          isOpen={isComposeOpen}
          onClose={() => setIsComposeOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Notification Card Component (for status updates, assignments, etc.)
 */
function NotificationCard({ notification, onMarkRead }) {
  const { t } = useTranslation('attorney');
  const isUnread = !notification.read && !notification.readAt;

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'status_change':
        return '📋';
      case 'assignment':
        return '👤';
      case 'deadline':
        return '⏰';
      case 'correspondence':
        return '📧';
      case 'approval':
        return '✅';
      case 'filing':
        return '📁';
      case 'client_response':
        return '💬';
      default:
        return '🔔';
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'status_change':
        return t('attorney.notifications.statusChange', 'Status Update');
      case 'assignment':
        return t('attorney.notifications.assignment', 'Assignment');
      case 'deadline':
        return t('attorney.notifications.deadline', 'Deadline');
      case 'correspondence':
        return t('attorney.notifications.correspondence', 'Correspondence');
      case 'approval':
        return t('attorney.notifications.approval', 'Approval');
      case 'filing':
        return t('attorney.notifications.filing', 'Filing');
      case 'client_response':
        return t('attorney.notifications.clientResponse', 'Client Response');
      default:
        return t('attorney.notifications.system', 'System');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return t('attorney.notifications.justNow', 'Just now');
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const handleClick = () => {
    if (isUnread) {
      onMarkRead();
    }
  };

  return (
    <div
      className={`notification-card ${isUnread ? 'unread' : ''}`}
      onClick={handleClick}
    >
      <div className="notification-icon">
        {getCategoryIcon(notification.category)}
      </div>
      <div className="notification-content">
        <div className="notification-header">
          <span className="notification-category">{getCategoryLabel(notification.category)}</span>
          <span className="notification-time">{formatDate(notification.createdAt)}</span>
        </div>
        <h4 className="notification-title">{notification.displayTitle}</h4>
        <p className="notification-message">{notification.displayMessage}</p>
        {notification.linkType && notification.linkId && (
          <button className="notification-action">
            {t('attorney.notifications.viewDetails', 'View details')} →
          </button>
        )}
      </div>
      {isUnread && <span className="unread-dot" />}
    </div>
  );
}

/**
 * Message Card Component (for client messages)
 */
function MessageCard({ message, onMarkRead }) {
  const { t } = useTranslation('attorney');
  const [expanded, setExpanded] = useState(false);
  const isUnread = !message.read && !message.readAt;

  const statusColors = {
    pending: 'status-pending',
    responded: 'status-success',
    expired: 'status-error'
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleToggle = () => {
    if (isUnread && !expanded) {
      onMarkRead();
    }
    setExpanded(!expanded);
  };

  return (
    <div className={`message-card ${expanded ? 'expanded' : ''} ${isUnread ? 'unread' : ''}`}>
      <div className="message-card-header" onClick={handleToggle}>
        <div className="message-info">
          <span className={`message-status ${statusColors[message.responseStatus] || ''}`}>
            {message.responseStatus === 'pending' ? '📨' : '✅'}
          </span>
          <div className="message-meta">
            <span className="message-title">{message.displayTitle || message.title}</span>
            <span className="message-application">
              {t('attorney.clientMessages.re', 'Re')}: {message.applicationId || 'Application'}
            </span>
          </div>
        </div>
        <div className="message-header-right">
          {message.responseStatus === 'pending' && (
            <span className="response-badge">{t('attorney.clientMessages.awaitingResponse', 'Awaiting Response')}</span>
          )}
          <span className="message-date">
            {formatDate(message.createdAt)}
          </span>
          {isUnread && <span className="unread-dot" />}
          <span className="expand-icon">{expanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {expanded && (
        <div className="message-card-body">
          <div className="message-content">
            <h4>{t('attorney.clientMessages.yourMessage', 'Your Message')}</h4>
            <p>{message.displayMessage || message.message}</p>
          </div>

          {message.requestData?.items && (
            <div className="message-items">
              <h4>{t('attorney.clientMessages.requestedItems', 'Requested Items')}</h4>
              <ul>
                {message.requestData.items.map((item, idx) => (
                  <li key={idx} className={item.response ? 'item-responded' : ''}>
                    <span className="item-label">{item.label}</span>
                    {item.response && (
                      <span className="item-response">{item.response}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {message.responseAttachments?.length > 0 && (
            <div className="message-attachments">
              <h4>{t('attorney.clientMessages.clientAttachments', 'Client Attachments')}</h4>
              <ul>
                {message.responseAttachments.map((att, idx) => (
                  <li key={idx}>
                    <a href={att.blobUrl} target="_blank" rel="noopener noreferrer">
                      📎 {att.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {message.responseStatus === 'responded' && message.respondedAt && (
            <div className="response-info">
              <span className="response-success">✅ {t('attorney.clientMessages.clientResponded', 'Client responded')}</span>
              <span className="response-date">{formatDate(message.respondedAt)}</span>
            </div>
          )}

          <div className="message-actions">
            <button className="btn btn-secondary">
              {t('attorney.clientMessages.viewThread', 'View Thread')}
            </button>
            {message.responseStatus === 'responded' && message.metadata?.originatedFromInternalRequest && (
              <button className="btn btn-primary">
                {t('attorney.clientMessages.forwardToExternal', 'Forward to External Attorney')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientMessages;
